import {
  collection, doc, onSnapshot, setDoc, Timestamp,
  type DocumentData, type Firestore, type Unsubscribe,
} from 'firebase/firestore';
import { doc as restDoc, getDoc as restGetDoc, type Firestore as RestFirestore } from 'firebase/firestore/lite';
import type { WireFrame, WireMap } from './outputViewCodec';
import { OutputViewStream, type OutputViewMapState, type OutputViewWireReport } from './outputViewStream';
import type { OutputViewSession } from './types';

export type {
  OutputViewMapState, OutputViewWireReason, OutputViewWireReport, OutputViewWireState,
} from './outputViewStream';
export type OutputViewDuration = 'default' | 'extended' | 'livegate';
/** A browser tab hidden this long ends the stream (owner, 2026-09-25: 30 s, was 10 s). */
export const OUTPUT_VIEW_HIDDEN_GRACE_MS = 30_000;
const RENEW_INTERVAL_MS = 150_000;
/**
 * How often the answer is read over REST while it has not arrived (`readAnswer`): at most 120 reads inside the
 * 30 s connect window.
 */
const ANSWER_READ_MS = 250;
export type OutputViewEndReason =
  | 'client_end' | 'connect_timeout' | 'device_revoked'
  | 'lease_expired' | 'membership_revoked' | 'operator_end' | 'session_ceiling'
  | 'link_dead' | 'superseded' | 'taken_over' | 'time_limit'
  | 'transport_closed' | 'transport_policy';

export interface OutputViewGrant {
  busy: false;
  sessionId: string;
  ticket: string;
  expiresAt: number;
  hardExpiresAt: number;
  signalingPath: string;
  iceServers: Array<{ urls: string; username?: string; credential?: string }>;
}

export interface OutputViewKey {
  publicKey: string;
  privateKey: CryptoKey;
}

function base64(bytes: Uint8Array): string {
  let raw = '';
  for (const byte of bytes) raw += String.fromCharCode(byte);
  return btoa(raw);
}

export async function createOutputViewKey(): Promise<OutputViewKey> {
  const pair = await crypto.subtle.generateKey('Ed25519', false, ['sign', 'verify']) as CryptoKeyPair;
  const raw = new Uint8Array(await crypto.subtle.exportKey('raw', pair.publicKey));
  if (raw.length !== 32 || pair.privateKey.extractable) throw new Error('Output view key generation failed');
  return { publicKey: base64(raw), privateKey: pair.privateKey };
}

export function relayIceConfiguration(servers: OutputViewGrant['iceServers']): RTCConfiguration {
  if (!servers.some((server) => /^turns?:\S+$/i.test(server.urls)
      && Boolean(server.username && server.credential))
      || servers.some((server) => /^turns?:\S+$/i.test(server.urls)
        ? !server.username || !server.credential
        : !/^stun:\S+$/i.test(server.urls) || Boolean(server.username || server.credential))) {
    throw new Error('Output view requires relay credentials');
  }
  return { iceServers: servers.map(({ urls, username, credential }) => username && credential
    ? { urls, username, credential } : { urls }),
    iceTransportPolicy: 'relay' };
}

export function isNotMemberRefusal(error: unknown): boolean {
  const value = error as { details?: unknown; message?: unknown } | null;
  const details = value?.details;
  return (typeof details === 'object' && details !== null && (details as { reason?: unknown }).reason === 'not-a-member')
    || details === 'not-a-member'
    || (typeof value?.message === 'string' && /\bnot-a-member\b/.test(value.message));
}

function ticketNonce(ticket: string): string {
  const part = ticket.split('.')[1];
  if (!part) throw new Error('Output view ticket is invalid');
  const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as { nonce?: unknown };
  if (typeof json.nonce !== 'string') throw new Error('Output view ticket is invalid');
  return json.nonce;
}

function nowUs(): bigint { return BigInt(Math.round((performance.timeOrigin + performance.now()) * 1000)); }

/**
 * A connection-setup milestone the peer sees, for the store's phase timings (measured, never gated).
 * `offerCandidatesComplete` is the end of this browser's ICE gathering: every candidate has trickled out by then.
 */
export type OutputViewPeerPhase = 'offerWritten' | 'offerCandidatesComplete' | 'answerReceived' | 'connected' | 'channelOpen';
/** Which path delivered the controller's answer: the Firestore listener or the REST read every 250 ms. */
export type OutputViewAnswerVia = 'listener' | 'read';

interface PeerCallbacks {
  /**
   * The controller's helloAck: the stream is live. `hardExpiresAt` is the ticket ceiling in epoch seconds
   * on the controller's clock; `clockOffsetUs` is the controller's clock minus this browser's.
   */
  onReady: (duration: OutputViewDuration, clockOffsetUs: bigint, hardExpiresAt: number) => void;
  onFrame: (frame: WireFrame, clockOffsetUs: bigint) => void;
  onMap: (map: WireMap | null) => void;
  onEnd: (reason: OutputViewEndReason, byDisplayName?: string) => void;
  onWire: (report: OutputViewWireReport) => void;
  onMapState?: (state: OutputViewMapState | null) => void;
  onPhase?: (mark: OutputViewPeerPhase, answerVia?: OutputViewAnswerVia) => void;
  renew: () => Promise<{ ticket: string; iceServers: OutputViewGrant['iceServers'] }>;
}

interface ControlMessage { type?: string; [key: string]: unknown }

export class OutputViewPeer {
  private readonly pc: RTCPeerConnection;
  private readonly control: RTCDataChannel;
  private readonly frames: RTCDataChannel;
  private readonly stream: OutputViewStream;
  private readonly unsubscribers: Unsubscribe[] = [];
  private readonly candidateQueue: RTCIceCandidateInit[] = [];
  private candidateCount = 0;
  private heartbeatSeq = 0;
  private clockOffsetUs = 0n;
  private finished = false;
  private acknowledged = false;
  private answerTaken = false;
  private answerReading = false;
  private answerTimer?: ReturnType<typeof setInterval>;
  /**
   * The largest (arrival − `updatedAt`) over this viewer's own session snapshots, in ms of this browser's
   * clock against the cloud's; `listenerLagMs` corrects it with the controller's clock offset once known.
   */
  private rawListenerLagMs?: number;
  private heartbeatTimer?: ReturnType<typeof setInterval>;
  private renewTimer?: ReturnType<typeof setInterval>;
  private renewPending = false;
  private hiddenTimer?: ReturnType<typeof setTimeout>;
  private connectTimer?: ReturnType<typeof setTimeout>;
  private pairTimer?: ReturnType<typeof setInterval>;
  private pairUnknownSince?: number;
  private pairVerified = false;
  private t1 = 0n;
  private readonly nonce: string;

  /**
   * `restDb` is the same app's `firebase/firestore/lite` instance (one HTTPS request per read, outside the listen
   * connection), handed in from `lib/firebase` so it is the instance emulator mode already pointed at the
   * emulator, whatever order modules were imported in.
   */
  constructor(
    private readonly db: Firestore,
    private readonly restDb: RestFirestore,
    private readonly grant: OutputViewGrant,
    private readonly key: OutputViewKey,
    private readonly callbacks: PeerCallbacks,
  ) {
    this.nonce = ticketNonce(grant.ticket);
    this.stream = new OutputViewStream({
      onFrame: (frame, offsetUs) => this.callbacks.onFrame(frame, offsetUs),
      onMap: (map) => this.callbacks.onMap(map),
      onWire: (report) => this.callbacks.onWire(report),
      onMapState: (state) => this.callbacks.onMapState?.(state),
      sendReady: (mapDigest) => this.send({ type: 'ready', mapDigest }),
    }, () => this.clockOffsetUs);
    this.pc = new RTCPeerConnection(relayIceConfiguration(grant.iceServers));
    this.control = this.pc.createDataChannel('output-view-control', { ordered: true, protocol: 'output-view/2' });
    this.frames = this.pc.createDataChannel('output-view-frames', { ordered: false, protocol: 'output-view/2' });
    this.frames.binaryType = 'arraybuffer';
    this.control.onopen = () => { this.callbacks.onPhase?.('channelOpen'); this.hello(); };
    this.control.onmessage = (event) => { void this.controlMessage(event.data); };
    this.control.onclose = () => { if (!this.finished) this.finish(this.acknowledged ? 'transport_closed' : 'connect_timeout'); };
    this.frames.onmessage = (event) => { void this.stream.frame(event.data); };
    // Trickle: each candidate is written the moment it is gathered, beside the offer's own write and never
    // behind its acknowledgement. The controller answers from the offer alone (it need carry no candidates) and
    // reads `candidates/desktop/entries` after answering. A null candidate is the end of gathering: a mark only,
    // since the contract defines no desktop end-of-candidates entry.
    this.pc.onicecandidate = (event) => {
      if (this.finished) return;
      if (event.candidate) void this.sendCandidate(event.candidate);
      else this.callbacks.onPhase?.('offerCandidatesComplete');
    };
    this.pc.oniceconnectionstatechange = () => {
      if (this.pc.iceConnectionState === 'failed') this.finish('connect_timeout');
    };
    this.pc.onconnectionstatechange = () => {
      if (this.pc.connectionState === 'failed') this.finish('connect_timeout');
      if (this.pc.connectionState === 'connected') {
        this.callbacks.onPhase?.('connected');
        void this.checkPair();
      }
    };
  }

  async start(): Promise<void> {
    const base = this.grant.signalingPath;
    this.unsubscribers.push(onSnapshot(doc(this.db, `${base}/signaling/answer`),
      (snap) => this.takeAnswer(snap.data(), 'listener'), () => this.finish('transport_closed')));
    this.unsubscribers.push(onSnapshot(collection(this.db, `${base}/candidates/device/entries`), (snap) => {
      for (const change of snap.docChanges()) {
        if (change.type !== 'added' || this.finished) continue;
        const candidate = change.doc.data() as RTCIceCandidateInit;
        if (typeof candidate.candidate !== 'string') { this.finish('transport_closed'); return; }
        const value: RTCIceCandidateInit = {
          candidate: candidate.candidate, sdpMid: candidate.sdpMid,
          sdpMLineIndex: candidate.sdpMLineIndex, usernameFragment: candidate.usernameFragment,
        };
        if (this.pc.remoteDescription) void this.pc.addIceCandidate(value).catch(() => this.finish('transport_closed'));
        else this.candidateQueue.push(value);
      }
    }, () => this.finish('transport_closed')));
    this.unsubscribers.push(onSnapshot(doc(this.db, base), (snap) => {
      const state = snap.data() as OutputViewSession | undefined;
      if (this.finished) return;
      if (typeof state?.updatedAt === 'number') {
        const lag = Date.now() - state.updatedAt * 1000;
        this.rawListenerLagMs = Math.max(this.rawListenerLagMs ?? lag, lag);
      }
      if (state?.state !== 'ENDED') return;
      if (state.endReason === 'taken-over') {
        if (typeof state.endedByDisplayName !== 'string' || !state.endedByDisplayName) {
          this.finish('transport_closed'); return;
        }
        this.finish('taken_over', state.endedByDisplayName);
        return;
      }
      const reason = state.endReason === 'ended-by-requester' ? 'client_end' : state.endReason;
      this.finish((reason || 'transport_closed') as OutputViewEndReason);
    }, () => this.finish('transport_closed')));
    document.addEventListener('visibilitychange', this.visibilityChanged);
    this.visibilityChanged();
    this.connectTimer = setTimeout(() => this.finish('connect_timeout'), 30_000);
    this.pairTimer = setInterval(() => { void this.checkPair(); }, 5_000);
    try {
      const offer = await this.pc.createOffer();
      if (this.finished || !offer.sdp) { this.finish('transport_closed'); return; }
      await this.pc.setLocalDescription(offer);
      if (this.finished || !this.pc.localDescription?.sdp) { this.finish('transport_closed'); return; }
      // Written the moment the local description is set: gathering has only just started, and the relay
      // candidates follow on their own writes (`onicecandidate`).
      await setDoc(doc(this.db, `${base}/signaling/offer`), {
        type: 'offer', sdp: this.pc.localDescription.sdp, ...this.signalTimes(),
      });
      if (this.finished) return;
      this.callbacks.onPhase?.('offerWritten');
      if (!this.answerTaken) this.answerTimer = setInterval(() => { void this.readAnswer(); }, ANSWER_READ_MS);
    } catch {
      this.finish('transport_closed');
    }
  }

  /** The controller's answer, from whichever path delivers it first; a second delivery changes nothing. */
  private takeAnswer(data: DocumentData | undefined, via: OutputViewAnswerVia): void {
    if (data?.type !== 'answer' || this.finished || this.answerTaken) return;
    if (typeof data.sdp !== 'string') { this.finish('transport_closed'); return; }
    this.answerTaken = true;
    this.stopAnswerReads();
    this.callbacks.onPhase?.('answerReceived', via);
    void this.pc.setRemoteDescription({ type: 'answer', sdp: data.sdp })
      .then(async () => {
        for (const candidate of this.candidateQueue.splice(0)) await this.pc.addIceCandidate(candidate);
      }).catch(() => this.finish('transport_closed'));
  }

  /**
   * The answer normally arrives on the listener. Firestore can hold one listen connection server-side for
   * 7–25 s while one-off REST reads keep flowing (2026-10-21: the controller wrote its answer 0.5 s after the
   * offer and the listener delivered it 19.5 s later; prod, ovs_ImBr7AhQaIPtd6-q, went live 26 s after the
   * answer; CONTRACT-console H49). `getDocFromServer` rides that same listen connection, so it was held too. The answer is therefore
   * also read over REST (`firebase/firestore/lite` `getDoc`, the same rules) every 250 ms from the offer
   * until an answer is taken, helloAck arrives or the peer ends; whichever path delivers first wins. The 30 s
   * connect timeout still bounds the wait; a failed read is simply tried again on the next tick.
   */
  private async readAnswer(): Promise<void> {
    if (this.finished || this.answerTaken || this.answerReading) return;
    this.answerReading = true;
    try {
      const snap = await restGetDoc(restDoc(this.restDb, `${this.grant.signalingPath}/signaling/answer`));
      this.takeAnswer(snap.data(), 'read');
    } catch { /* Retried on the next tick; the listener and the connect timeout still decide. */ }
    finally { this.answerReading = false; }
  }

  private stopAnswerReads(): void {
    if (this.answerTimer) clearInterval(this.answerTimer);
    this.answerTimer = undefined;
  }

  /**
   * The largest lag of this viewer's session listener so far (snapshot arrival minus the broker's
   * `updatedAt`, 1 s resolution), on the controller's clock once helloAck has given the offset; null before
   * any snapshot carried `updatedAt`.
   */
  listenerLagMs(): number | null {
    if (this.rawListenerLagMs === undefined) return null;
    return Math.round(this.rawListenerLagMs + Number(this.clockOffsetUs) / 1000);
  }

  private signalTimes(): { expiresAt: number; purgeAt: Timestamp } {
    return { expiresAt: this.grant.expiresAt, purgeAt: Timestamp.fromMillis(this.grant.expiresAt * 1000) };
  }

  private async sendCandidate(candidate: RTCIceCandidate): Promise<void> {
    if (this.finished) return;
    if (this.candidateCount >= 63) return;
    const id = String(this.candidateCount++);
    const data = candidate.toJSON();
    try {
      await setDoc(doc(this.db, `${this.grant.signalingPath}/candidates/desktop/entries/${id}`), {
        candidate: data.candidate, sdpMid: data.sdpMid ?? null,
        sdpMLineIndex: data.sdpMLineIndex ?? null, usernameFragment: data.usernameFragment ?? null,
        ...this.signalTimes(),
      });
    } catch { this.finish('transport_closed'); }
  }

  private send(message: ControlMessage): void {
    if (this.control.readyState === 'open') this.control.send(JSON.stringify(message));
  }

  private hello(): void {
    this.t1 = nowUs();
    this.send({ type: 'hello', version: 'output-view/2', sessionId: this.grant.sessionId,
      ticket: this.grant.ticket, clientTimeUs: String(this.t1) });
  }

  private async controlMessage(raw: unknown): Promise<void> {
    if (this.finished || typeof raw !== 'string') return;
    let message: ControlMessage;
    try { message = JSON.parse(raw) as ControlMessage; } catch { return; }
    if (message.type === 'helloChallenge') {
      try {
        if (message.version !== 'output-view/2' || message.sessionId !== this.grant.sessionId
          || message.clientTimeUs !== String(this.t1)
          || typeof message.deviceChallenge !== 'string' || !/^[A-Za-z0-9_-]{43}$/.test(message.deviceChallenge)) {
          this.finish('transport_closed'); return;
        }
        const t2 = BigInt(String(message.deviceReceiveTimeUs));
        const t3 = BigInt(String(message.deviceSendTimeUs));
        const t4 = nowUs();
        const offset = ((t2 - this.t1) + (t3 - t4)) / 2n;
        const rtt = (t4 - this.t1) - (t3 - t2);
        const clampedRtt = rtt < 0n ? 0n : rtt;
        const proof = [
          'output-view/2 hello-proof', `sessionId=${this.grant.sessionId}`,
          `nonce=${this.nonce}`, `deviceChallenge=${message.deviceChallenge}`,
          `clientTimeUs=${this.t1}`, `deviceReceiveTimeUs=${t2}`,
          `deviceSendTimeUs=${t3}`, `clientReceiveTimeUs=${t4}`,
          `clockOffsetUs=${offset}`, `rttUs=${clampedRtt}`,
        ].join('\n');
        const signature = new Uint8Array(await crypto.subtle.sign('Ed25519', this.key.privateKey, new TextEncoder().encode(proof)));
        this.send({ type: 'helloProof', version: 'output-view/2', sessionId: this.grant.sessionId,
          signature: base64(signature), clientReceiveTimeUs: String(t4), clockOffsetUs: String(offset), rttUs: String(clampedRtt) });
      } catch { this.finish('transport_closed'); }
      return;
    }
    if (message.type === 'helloAck') {
      if (this.acknowledged || message.version !== 'output-view/2' || message.sessionId !== this.grant.sessionId
        || (message.duration !== 'default' && message.duration !== 'extended' && message.duration !== 'livegate')
        || typeof message.hardExpiresAt !== 'number' || !Number.isSafeInteger(message.hardExpiresAt)
        || message.hardExpiresAt <= 0) return;
      this.acknowledged = true;
      this.clockOffsetUs = BigInt(String(message.clockOffsetUs));
      if (this.connectTimer) clearTimeout(this.connectTimer);
      this.stopAnswerReads();
      this.callbacks.onReady(message.duration, this.clockOffsetUs, message.hardExpiresAt);
      this.stream.begin();
      this.send({ type: 'heartbeat', seq: this.heartbeatSeq++ });
      this.heartbeatTimer = setInterval(() => this.send({ type: 'heartbeat', seq: this.heartbeatSeq++ }), 5_000);
      if (message.duration === 'extended') this.renewTimer = setInterval(() => { void this.renew(); }, RENEW_INTERVAL_MS);
      if (message.duration === 'livegate') this.renewTimer = setTimeout(() => { void this.renew(); }, 3_000);
      return;
    }
    if (!this.acknowledged) return;
    if (this.stream.control(message)) return;
    if (message.type === 'end') {
      this.finish(message.reason as OutputViewEndReason);
    }
  }

  /**
   * Hand the Pi a duration-switch ticket (setOutputViewSessionDuration) on the existing `grant` message and
   * run the renewal clock the new duration needs: every 150 s for extended, none for default.
   */
  changeDuration(duration: 'default' | 'extended', grant: { ticket: string; iceServers: OutputViewGrant['iceServers'] }): void {
    if (this.finished) return;
    this.pc.setConfiguration(relayIceConfiguration(grant.iceServers));
    this.send({ type: 'grant', ticket: grant.ticket });
    if (this.renewTimer) clearInterval(this.renewTimer);
    this.renewTimer = undefined;
    this.renewPending = false;
    if (duration === 'extended') this.renewTimer = setInterval(() => { void this.renew(); }, RENEW_INTERVAL_MS);
  }

  private async renew(): Promise<void> {
    if (this.finished) return;
    // Renewal happens only while the tab is visible. One that falls due while hidden runs the moment the
    // tab is visible again, so a hide shorter than the grace never costs the 300 s lease.
    if (document.visibilityState !== 'visible') { this.renewPending = true; return; }
    this.renewPending = false;
    try {
      const grant = await this.callbacks.renew();
      this.pc.setConfiguration(relayIceConfiguration(grant.iceServers));
      this.send({ type: 'grant', ticket: grant.ticket });
    }
    catch (error) { this.finish(isNotMemberRefusal(error) ? 'membership_revoked' : 'lease_expired'); }
  }

  private async checkPair(): Promise<void> {
    if (this.finished || this.pc.connectionState !== 'connected') return;
    try {
      const stats = await this.pc.getStats();
      const rows = new Map<string, Record<string, unknown>>();
      stats.forEach((value) => rows.set(value.id, value as unknown as Record<string, unknown>));
      const transport = [...rows.values()].find((row) => row.type === 'transport' && typeof row.selectedCandidatePairId === 'string');
      const pair = transport ? rows.get(String(transport.selectedCandidatePairId))
        : [...rows.values()].find((row) => row.type === 'candidate-pair' && (row.selected === true || row.nominated === true));
      if (!pair) {
        this.pairUnknownSince ??= Date.now();
        if (Date.now() - this.pairUnknownSince >= 5_000) this.finish('transport_policy');
        return;
      }
      const local = rows.get(String(pair.localCandidateId));
      const remote = rows.get(String(pair.remoteCandidateId));
      if (!local || !remote) {
        this.pairUnknownSince ??= Date.now();
        if (Date.now() - this.pairUnknownSince >= 5_000) this.finish('transport_policy');
        return;
      }
      this.pairUnknownSince = undefined;
      if (local.candidateType !== 'relay' || remote.candidateType !== 'relay') {
        this.finish('transport_policy');
        return;
      }
      if (!this.pairVerified) {
        this.pairVerified = true;
        console.info('[nuance-venue] output view pair selected', { connectionKind: 'TURN' });
      }
    } catch { this.finish('transport_policy'); }
  }

  private visibilityChanged = (): void => {
    if (document.visibilityState === 'hidden') {
      if (this.hiddenTimer) clearTimeout(this.hiddenTimer);
      this.hiddenTimer = setTimeout(() => { if (document.visibilityState === 'hidden') this.stop(); }, OUTPUT_VIEW_HIDDEN_GRACE_MS);
    } else {
      if (this.hiddenTimer) clearTimeout(this.hiddenTimer);
      this.hiddenTimer = undefined;
      if (this.renewPending) void this.renew();
    }
  };

  stop(): void {
    this.send({ type: 'end', reason: 'client_end' });
    this.finish('client_end');
  }

  private finish(reason: OutputViewEndReason, byDisplayName?: string): void {
    if (this.finished) return;
    this.finished = true;
    for (const unsubscribe of this.unsubscribers) unsubscribe();
    document.removeEventListener('visibilitychange', this.visibilityChanged);
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    if (this.renewTimer) clearInterval(this.renewTimer);
    if (this.hiddenTimer) clearTimeout(this.hiddenTimer);
    if (this.connectTimer) clearTimeout(this.connectTimer);
    if (this.pairTimer) clearInterval(this.pairTimer);
    this.stream.stop();
    this.stopAnswerReads();
    this.frames.close();
    this.control.close();
    this.pc.close();
    this.callbacks.onEnd(reason, byDisplayName);
  }
}
